import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';

/**
 * Lets a user dismiss the floating support launcher (the X on its corner) and
 * bring it back from the profile menu's "Help & Support" item.
 *
 * The choice is per user and per browser (localStorage), so it survives
 * reloads and re-logins but never leaks to another user on the same machine.
 * HomeComponent (profile menu) and SupportChatWidgetComponent (the launcher)
 * are siblings, so they talk through this service rather than inputs.
 */
@Injectable({ providedIn: 'root' })
export class SupportWidgetVisibilityService {
  private userLoginService = inject(UserLoginService);

  private readonly hiddenSubject = new BehaviorSubject<boolean>(this.read());
  readonly hidden$ = this.hiddenSubject.asObservable();

  // Emits when "Help & Support" is picked: the widget shows itself and opens.
  private readonly openRequestSubject = new Subject<void>();
  readonly openRequest$ = this.openRequestSubject.asObservable();

  get hidden(): boolean {
    return this.hiddenSubject.value;
  }

  // The service can be created before the user logs in; re-read for whoever is
  // signed in now.
  refresh(): void {
    this.hiddenSubject.next(this.read());
  }

  hide(): void {
    this.write(true);
    this.hiddenSubject.next(true);
  }

  // Un-hides the launcher without opening it (new support activity).
  restore(): void {
    if (!this.hidden) return;
    this.write(false);
    this.hiddenSubject.next(false);
  }

  // Un-hides the launcher (if needed) and asks it to open its panel.
  requestOpen(): void {
    if (this.hidden) {
      this.write(false);
      this.hiddenSubject.next(false);
    }
    this.openRequestSubject.next();
  }

  private storageKey(): string {
    return `adv_support_widget_hidden_${this.userLoginService.User()?.userId ?? 'anon'}`;
  }

  private read(): boolean {
    try {
      return localStorage.getItem(this.storageKey()) === '1';
    } catch {
      return false;
    }
  }

  private write(hidden: boolean): void {
    try {
      if (hidden) localStorage.setItem(this.storageKey(), '1');
      else localStorage.removeItem(this.storageKey());
    } catch {
      // Storage blocked (private mode etc.): the choice just won't persist.
    }
  }
}
